import { useEffect, useState, Fragment } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { ArrowForwardRounded, Warning } from '@mui/icons-material';
import CircularLoader from 'ui-component/CircularLoader';
import PropTypes from 'prop-types';

const DIALOG_SIZES = ['xs', 'sm', 'md', 'lg', 'xl'];
const MESSAGE_MAX_WIDTHS = {
    xs: 380,
    sm: 500,
    md: 760,
    lg: 1000,
    xl: 1280
};

/**
 * @params
 *  isOpen (Boolean) = set modal open
 *  size (String) = optional Material UI dialog size; defaults to current xs size
 * onClose (Function) = set parent toggle to falsess
 */
export default function ConfirmationModal({ data, onClose }) {
    const [open, setOpen] = useState(false);
    const {
        isOpen,
        title,
        message,
        handleProceed,
        isProceed,
        fnClose,
        content,
        type = 'confirmation',
        confirmToProceed = false,
        confirmToProceedLabel,
        proceedLabel = 'Save and continue?',
        cancelLabel = 'Cancel',
        messageSx,
        size = 'xs'
    } = data;
    const dialogSize = DIALOG_SIZES.includes(size) ? size : 'xs';
    const [loading, setLoading] = useState(false);
    const [confirm, setConfirm] = useState(false);

    const modalTheme = {
        cancel: {
            primary: '#f65f5f',
            hover: '#f65f5feb',
            light: '#fff1f1',
            lighter: '#fff8f8'
        },
        proceed: {
            primary: '#2ca58d',
            hover: '#298270',
            light: '#e7f7f3',
            lighter: '#f5fbf9'
        }
    };

    let colorVariant;
    switch (type) {
        case 'confirmation':
            colorVariant = modalTheme['proceed'];
            break;
        case 'confirmDelete':
            colorVariant = modalTheme['cancel'];
            break;
        default:
            colorVariant = modalTheme['proceed'];
            break;
    }

    useEffect(() => {
        setLoading(isProceed);
    }, [isProceed]);

    useEffect(() => {
        setOpen(isOpen || false);
    }, [isOpen]);

    const handleClose = () => {
        setOpen(false);
        onClose();

        if (typeof fnClose !== 'undefined') {
            fnClose(); // handle from your component if cancel was click
        }
    };

    return (
        <Fragment>
            <Dialog
                open={open || false}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                maxWidth={dialogSize}
                fullWidth
                PaperProps={{
                    sx: {
                        width: '100%',
                        maxWidth: (theme) => (dialogSize === 'xs' ? 520 : theme.breakpoints.values[dialogSize]),
                        m: 2,
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: '0 24px 70px rgba(15, 23, 42, 0.28)',
                        backgroundImage: 'none'
                    }
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            bgcolor: 'rgba(15, 23, 42, 0.58)',
                            backdropFilter: 'blur(2px)'
                        }
                    }
                }}
            >
                <DialogContent
                    sx={{
                        position: 'relative',
                        px: { xs: 3, sm: 5 },
                        pt: { xs: 4, sm: 5 },
                        pb: 2,
                        textAlign: 'center',
                        overflow: 'hidden'
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            zIndex: 1
                        }}
                    >
                        <Box
                            sx={{
                                width: 112,
                                height: 112,
                                mx: 'auto',
                                mb: 3,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: colorVariant.light,
                                boxShadow: `0 0 0 14px ${colorVariant.lighter}`
                            }}
                        >
                            {type === 'confirmDelete' ? (
                                <Warning sx={{ color: colorVariant.primary, fontSize: '4rem' }} />
                            ) : (
                                <Typography sx={{ color: colorVariant.primary, fontSize: '4rem', fontWeight: 700, lineHeight: 1 }}>
                                    ?
                                </Typography>
                            )}
                        </Box>

                        {title && (
                            <Typography
                                id="alert-dialog-title"
                                component="h2"
                                sx={{
                                    fontSize: {
                                        xs: '1.55rem',
                                        sm: '1.85rem'
                                    },
                                    fontWeight: 700,
                                    lineHeight: 1.2,
                                    color: 'text.primary'
                                }}
                            >
                                {title}
                            </Typography>
                        )}

                        <Box
                            sx={{
                                width: 48,
                                height: 3,
                                borderRadius: 999,
                                bgcolor: colorVariant.primary,
                                mx: 'auto',
                                my: 2
                            }}
                        />

                        {message && (
                            <Typography
                                component="div"
                                id="alert-dialog-description"
                                sx={{
                                    maxWidth: MESSAGE_MAX_WIDTHS[dialogSize],
                                    mx: 'auto',
                                    fontSize: '0.95rem',
                                    lineHeight: 1.65,
                                    color: 'text.secondary',
                                    ...messageSx,
                                    textAlign: 'center'
                                }}
                            >
                                {message}
                            </Typography>
                        )}

                        {content && <Box sx={{ mt: 2.5, textAlign: 'left' }}>{content}</Box>}

                        {confirmToProceed && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5 }}>
                                <FormControlLabel
                                    control={<Checkbox checked={confirm} onChange={(e) => setConfirm(e.target.checked)} />}
                                    label={<Typography variant="body2">{confirmToProceedLabel || 'Accept and continue.'}</Typography>}
                                />
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions
                    sx={{
                        position: 'relative',
                        px: { xs: 3, sm: 5 },
                        pt: 2,
                        pb: { xs: 3, sm: 4 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        '& > :not(style) ~ :not(style)': {
                            ml: 0
                        }
                    }}
                >
                    <Button
                        onClick={async () => {
                            setLoading(true);
                            await handleProceed();
                            setLoading(false);
                        }}
                        autoFocus
                        variant="contained"
                        size="large"
                        disableElevation
                        // fullWidth
                        endIcon={type !== 'confirmDelete' && !loading ? <ArrowForwardRounded /> : undefined}
                        sx={{
                            width: { xs: '100%', md: '75%' },
                            minHeight: 48,
                            borderRadius: 2,
                            bgcolor: colorVariant.primary,
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            fontSize: '0.95rem',
                            textTransform: 'none',
                            boxShadow: `0 8px 20px ${colorVariant.primary}33`,
                            '&:hover': {
                                bgcolor: colorVariant.hover,
                                boxShadow: `0 10px 24px ${colorVariant.primary}45`
                            }
                        }}
                        disabled={(confirmToProceed && !confirm) || loading}
                    >
                        {loading ? (
                            <>
                                <CircularLoader size={24} minHeight="0rem" color="inherit" />
                                <Box paddingLeft={1}>Submitting</Box>
                            </>
                        ) : (
                            proceedLabel
                        )}
                    </Button>
                    <Button
                        onClick={handleClose}
                        size="large"
                        sx={{
                            color: modalTheme.proceed.primary,
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            fontSize: '0.95rem',
                            textTransform: 'none',
                            '&:hover': {
                                color: modalTheme.proceed.hover
                            }
                        }}
                        disabled={loading}
                    >
                        {cancelLabel}
                    </Button>
                </DialogActions>
            </Dialog>
        </Fragment>
    );
}

ConfirmationModal.propTypes = {
    data: PropTypes.any,
    onClose: PropTypes.func
};
